import { useQuery } from "@tanstack/react-query";

import { Clock, Dumbbell, Flame, ICON_STROKE, Sigma } from "@/shared/components/icons";
import { fetchProfileSummary } from "@/shared/api";

/** "Your coaching record" — the investment header that reframes the profile
 *  as an accruing asset. Hidden until there's any activity. */
export function InvestmentHeader() {
  const { data } = useQuery({ queryKey: ["profile-summary"], queryFn: fetchProfileSummary });
  const inv = data?.investment;
  if (!inv || inv.daysActive === 0) return null;

  return (
    <section className="rounded-2xl border border-border bg-surface p-5 space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold tracking-tight text-text">Your coaching record</h2>
        {inv.activeSince && (
          <span className="text-xs text-text-muted">
            Active since{" "}
            {new Date(inv.activeSince).toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
            })}{" "}
            · {inv.daysActive} day{inv.daysActive === 1 ? "" : "s"}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat Icon={Dumbbell} value={inv.drillsCompleted} label="drills" />
        <Stat Icon={Sigma} value={inv.signalsLogged} label="signals" />
        <Stat Icon={Flame} value={inv.currentStreak} label="day streak" />
        <Stat Icon={Clock} value={inv.longestStreak} label="best streak" />
      </div>
    </section>
  );
}

function Stat({
  Icon,
  value,
  label,
}: {
  Icon: typeof Dumbbell;
  value: number;
  label: string;
}) {
  return (
    <div className="rounded-xl border border-border/60 bg-bg/30 px-3 py-3 text-center">
      <Icon
        size={16}
        strokeWidth={ICON_STROKE}
        className="mx-auto mb-1 text-accent"
        aria-hidden
      />
      <div className="text-xl font-semibold tabular-nums text-text">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-text-muted mt-0.5">{label}</div>
    </div>
  );
}
